import React, { useEffect, useMemo, useState } from 'react';
import { Table, Icon, Button, Pagination, Breadcrumb, Form } from 'semantic-ui-react';

/**
 * columnsConfig soporta:
 * - key: string (id único de la columna)
 * - label: string
 * - width?: string
 * - sortable?: boolean (default true)
 * - accessor?: (row) => any       // para nested data, computed, etc
 * - render?: (value, row) => ReactNode // para formateos
 *
 * selection:
 * - selectedItems: array de ids (controlado desde el padre)
 * - getRowId: (row) => string|number  (default: row.id ?? row.variationId)
 */
const GenericTable = ({
  column,
  direction,
  onSort,
  columnsConfig = [],
  data = [],
  showCheckbox = true,
  selectedItems = [],
  setSelectedItems = () => {},
  actions = [], // [{ icon, color, onClick, title, disabled? }]
  onRowClick, // (row) => void
  rowKey, // (row) => string|number  (deprecated, usar getRowId)
  getRowId, // (row) => string|number
  rowClickable = true,
  emptyMessage = 'Sin datos',
  serverPagination = false,
  serverPage = 1,
  serverTotalPages = 1,
  onServerPageChange,
  serverPageSize = 20,
  onServerPageSizeChange,
  serverTotalItems = 0,
  tableMinWidth,
  tableStyle,
  tableContainerStyle,
  pageSizeStorageKey = 'genericTable.itemsPerPage',
  defaultItemsPerPage = 10,
  getRowStyle,
}) => {
  const readStoredItemsPerPage = () => {
    if (typeof window === 'undefined') return null;
    const saved = window.localStorage.getItem(pageSizeStorageKey)
      || (pageSizeStorageKey === 'genericTable.itemsPerPage' ? window.localStorage.getItem('itemsPerPage') : null);
    const parsed = saved ? parseInt(saved, 10) : null;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    return readStoredItemsPerPage() || defaultItemsPerPage;
  });

  // Evita quedarte en una página inválida si cambia data/itemsPerPage
  useEffect(() => {
    if (serverPagination) return;
    const totalPages = Math.max(1, Math.ceil((data?.length || 0) / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [data?.length, itemsPerPage, currentPage, serverPagination]);

  const resolvedGetRowId = useMemo(() => {
    if (typeof getRowId === 'function') return getRowId;
    if (typeof rowKey === 'function') return rowKey;
    return (row) => row?.id ?? row?.variationId;
  }, [getRowId, rowKey]);

  const totalPages = serverPagination
    ? Math.max(1, Number(serverTotalPages || 1))
    : Math.max(1, Math.ceil((data?.length || 0) / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = serverPagination
    ? (data || [])
    : (data || []).slice(startIndex, startIndex + itemsPerPage);

  const handlePaginationChange = (e, { activePage }) => {
    const nextPage = Number(activePage);
    if (!Number.isFinite(nextPage) || nextPage < 1) return;
    if (serverPagination) {
      if (typeof onServerPageChange === 'function') onServerPageChange(nextPage);
      return;
    }
    setCurrentPage(nextPage);
  };

  const handleItemsPerPageChange = (value) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(pageSizeStorageKey, String(value));
    }
    if (serverPagination) {
      if (typeof onServerPageSizeChange === 'function') onServerPageSizeChange(value);
      return;
    }
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const currentPageIds = useMemo(() => {
    return currentItems
      .map((row) => resolvedGetRowId(row))
      .filter((id) => id !== undefined && id !== null);
  }, [currentItems, resolvedGetRowId]);

  const allSelectedOnPage =
    currentPageIds.length > 0 && currentPageIds.every((id) => selectedItems.includes(id));

  // FIX: select-all debe actuar por página y por IDs, no por length
  const handleSelectAll = () => {
    if (currentPageIds.length === 0) return;

    if (allSelectedOnPage) {
      // quitar los de esta página
      setSelectedItems((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      // agregar los de esta página (sin duplicar)
      setSelectedItems((prev) => {
        const set = new Set(prev);
        currentPageIds.forEach((id) => set.add(id));
        return Array.from(set);
      });
    }
  };

  const handleCheckboxChange = (row) => {
    const id = resolvedGetRowId(row);
    if (id === undefined || id === null) return;

    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const itemsPerPageOptions = [10, 20, 30, 50, 100].map((n) => ({ key: n, value: n, text: String(n) }));

  const hasActions = actions && actions.length > 0;

  // FIX: colSpan debe contemplar checkbox + data cols + actions col
  const colSpan = (showCheckbox ? 1 : 0) + columnsConfig.length + (hasActions ? 1 : 0);

  const getCellValue = (row, col) => {
    if (typeof col.accessor === 'function') return col.accessor(row);
    return row?.[col.key];
  };

  const handleHeaderClick = (col) => {
    if (!col.sortable && col.sortable !== undefined) return; // sortable: false
    if (typeof onSort === 'function') onSort(col.key);
  };

  return (
    <>
      <div className="responsive-table-container" style={{ paddingBottom: 6, ...tableContainerStyle }}>
      <Table celled striped selectable style={{ minWidth: tableMinWidth, ...tableStyle }}>
        <Table.Header>
          <Table.Row>
            {showCheckbox && (
              <Table.HeaderCell textAlign="center" collapsing>
                <Button
                  basic
                  icon={allSelectedOnPage ? 'check square' : 'square outline'}
                  onClick={handleSelectAll}
                  title="Seleccionar todos (página actual)"
                />
              </Table.HeaderCell>
            )}

            {columnsConfig.map((col) => (
              <Table.HeaderCell
                key={col.key}
                sorted={column === col.key ? direction : null}
                onClick={() => handleHeaderClick(col)}
                style={{
                  width: col.width,
                  cursor: col.sortable === false ? 'default' : 'pointer',
                  whiteSpace: 'nowrap',
                  paddingTop: 14,
                  paddingBottom: 14,
                  background: '#F8FAFC',
                  color: '#111827',
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.01em',
                }}
              >
                {col.label}
                {column === col.key && (
                  <Icon name={direction === 'ascending' ? 'arrow up' : 'arrow down'} />
                )}
              </Table.HeaderCell>
            ))}

            {hasActions && <Table.HeaderCell textAlign="center">Acciones</Table.HeaderCell>}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {currentItems.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={colSpan} textAlign="center">
                {emptyMessage}
              </Table.Cell>
            </Table.Row>
          ) : (
            currentItems.map((row) => {
              const id = resolvedGetRowId(row);
              const clickable = rowClickable && typeof onRowClick === 'function';
              const rowStyle =
                typeof getRowStyle === 'function'
                  ? getRowStyle(row)
                  : clickable
                    ? { cursor: 'pointer' }
                    : undefined;

              return (
                <Table.Row
                  key={id ?? JSON.stringify(row)}
                  style={rowStyle}
                  onClick={
                    clickable
                      ? () => {
                          onRowClick(row);
                        }
                      : undefined
                  }
                >
                  {showCheckbox && (
                    <Table.Cell collapsing textAlign="center" onClick={(e) => e.stopPropagation()}>
                      <Form.Checkbox
                        checked={id !== undefined && selectedItems.includes(id)}
                        onChange={() => handleCheckboxChange(row)}
                      />
                    </Table.Cell>
                  )}

                  {columnsConfig.map((col) => {
                    const value = getCellValue(row, col);
                    const cellStyle = typeof col.getCellStyle === 'function'
                      ? col.getCellStyle(value, row)
                      : undefined;
                    return (
                      <Table.Cell key={col.key} style={{ verticalAlign: 'middle', ...cellStyle }}>
                        {typeof col.render === 'function' ? col.render(value, row) : String(value ?? '')}
                      </Table.Cell>
                    );
                  })}

                  {hasActions && (
                    <Table.Cell collapsing textAlign="center" onClick={(e) => e.stopPropagation()}>
                      {actions.map((action, aIdx) => (
                        <Button
                          key={aIdx}
                          circular
                          size="mini"
                          icon={action.icon}
                          color={action.color || 'grey'}
                          title={action.title || ''}
                          disabled={typeof action.disabled === 'function' ? action.disabled(row) : !!action.disabled}
                          onClick={() => action.onClick(row)}
                        />
                      ))}
                    </Table.Cell>
                  )}
                </Table.Row>
              );
            })
          )}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={colSpan}>
              <div className="ui small label">
                <Icon name="file alternate outline" /> Páginas
              </div>
              <Breadcrumb>
                {itemsPerPageOptions.map((opt, i) => (
                  <React.Fragment key={opt.key}>
                    <Breadcrumb.Section
                      active={(serverPagination ? serverPageSize : itemsPerPage) === opt.value}
                      onClick={() => handleItemsPerPageChange(opt.value)}
                      style={{ cursor: 'pointer', color: 'purple' }}
                    >
                      {opt.text}
                    </Breadcrumb.Section>
                    {i < itemsPerPageOptions.length - 1 && <Breadcrumb.Divider>/</Breadcrumb.Divider>}
                  </React.Fragment>
                ))}
              </Breadcrumb>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
        <Pagination
          activePage={serverPagination ? serverPage : currentPage}
          onPageChange={handlePaginationChange}
          totalPages={totalPages}
          ellipsisItem={null}
          firstItem={null}
          lastItem={null}
          siblingRange={1}
          color="purple"
        />
      </div>
      {serverPagination ? (
        <div style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
          Pagina {serverPage} de {totalPages} ({serverTotalItems} registros)
        </div>
      ) : null}
    </>
  );
};

export default GenericTable;
